import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useArticles } from '../hooks/useArticles';
import { useBatchStatusPolling } from '../hooks/useBatchStatusPolling';
import { useToast } from '../hooks/useToast';
import { setTitle } from '../utils/environment';
import AddUrlForm from '../components/AddUrlForm';
import ArticleCard from '../components/ArticleCard';
import Pagination from '../components/Pagination';
import SearchInput from '../components/SearchInput';
import SearchModal from '../components/SearchModal';
import TopicsWidget from '../components/TopicsWidget';
import DailyBriefingWidget from '../components/DailyBriefingWidget';
import DashboardHeader from './DashboardHeader';
import EmptyState from '../components/EmptyState';

const Dashboard = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [savedSearchState, setSavedSearchState] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const previousArticles = useRef([]);
  const fetchArticlesRef = useRef();
  const searchInputRef = useRef();
  const statsRefreshTimeoutRef = useRef(null);

  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { showSuccess, showError } = useToast();
  const {
    articles,
    stats,
    pagination,
    filters,
    isLoading,
    error,
    fetchArticles,
    fetchStats,
    setFilters,
    clearError,
    refreshArticles,
    goToPage,
    setPerPage,
    isEmpty,
  } = useArticles();

  // Enable batch status polling for pending/processing articles
  useBatchStatusPolling(3000, true);

  // Keep a stable reference to fetchArticles
  useEffect(() => {
    fetchArticlesRef.current = fetchArticles;
  }, [fetchArticles]);

  // Calculate unread count from stats (total across all pages), fallback to current page
  const unreadCount = stats?.unread_articles ?? articles.filter(article => !article.is_read).length;

  // Load articles on mount only
  useEffect(() => {
    // Set page title with environment prefix
    setTitle('Inbox · Read It Never');

    fetchArticles({ page: 1 }).catch(console.error);

    // Cleanup stats refresh timeout on unmount
    return () => {
      if (statsRefreshTimeoutRef.current) {
        clearTimeout(statsRefreshTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Background stats refresh when returning to dashboard
  useEffect(() => {
    // Always refresh stats when dashboard mounts to ensure up-to-date numbers
    // This is particularly important when returning from article viewer
    // where user may have marked articles as read/unread
    fetchStats(true).catch(console.error); // force=true for fresh data
  }, []); // Empty dependency - runs once on mount

  // Check for returning from search result and restore modal state
  useEffect(() => {
    const searchState = sessionStorage.getItem('searchModalState');
    if (searchState) {
      try {
        const parsedState = JSON.parse(searchState);
        setSavedSearchState(parsedState);
        setIsSearchModalOpen(true);
        // Clear from session storage to prevent reopening on future visits
        sessionStorage.removeItem('searchModalState');
      } catch (error) {
        console.error('Failed to restore search state:', error);
        sessionStorage.removeItem('searchModalState');
      }
    }
  }, []);

  // Update title when unread count changes
  useEffect(() => {
    if (unreadCount > 0) {
      setTitle(`Inbox (${unreadCount}) · Read It Never`);
    } else {
      setTitle('Inbox · Read It Never');
    }
  }, [unreadCount, stats]);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);

  // Check for newly completed articles and show notifications
  useEffect(() => {
    if (previousArticles.current.length > 0) {
      articles.forEach(article => {
        const previousArticle = previousArticles.current.find(prev => prev.id === article.id);
        if (previousArticle &&
            previousArticle.processing_status !== 'completed' &&
            article.processing_status === 'completed' &&
            article.title && article.title !== '') {
          showSuccess(`"${article.title}" finished processing!`);
        }
        if (previousArticle &&
            previousArticle.processing_status !== 'failed' &&
            article.processing_status === 'failed') {
          showError(`Failed to process article: ${article.url}`);
        }
      });
    }
    previousArticles.current = articles;
  }, [articles, showSuccess, showError]);

  // Update filters with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== filters.search || domainFilter !== filters.domain) {
        setFilters({
          search: searchQuery,
          domain: domainFilter,
        });
        // Only reset to page 1 when filters actually change, not when pagination changes
        fetchArticlesRef.current?.({ page: 1 }).catch(console.error);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, domainFilter, filters.search, filters.domain, setFilters]);

  const handleArticleAdded = useCallback(() => {
    // Show success message
    showSuccess('Article saved! Processing in background...');

    // Debounced stats refresh - cancel existing timeout if any
    if (statsRefreshTimeoutRef.current) {
      clearTimeout(statsRefreshTimeoutRef.current);
    }
    statsRefreshTimeoutRef.current = setTimeout(() => {
      fetchStats().catch(console.error);
      statsRefreshTimeoutRef.current = null;
    }, 100);

    // Optionally refresh the article list if needed
    if (articles.length === 0) {
      refreshArticles().catch(console.error);
    }
  }, [fetchStats, articles.length, refreshArticles, showSuccess]);

  const handleArticleDeleted = useCallback(() => {
    // Debounced stats refresh - cancel existing timeout if any
    if (statsRefreshTimeoutRef.current) {
      clearTimeout(statsRefreshTimeoutRef.current);
    }
    statsRefreshTimeoutRef.current = setTimeout(() => {
      fetchStats().catch(console.error);
      statsRefreshTimeoutRef.current = null;
    }, 100);
  }, [fetchStats]);

  const handleRefresh = useCallback(() => {
    refreshArticles().catch(console.error);
    // Force refresh stats
    fetchStats(true).catch(console.error);
  }, [refreshArticles, fetchStats]);

  const handlePageChange = useCallback((page) => {
    goToPage(page).catch(console.error);
  }, [goToPage]);

  const handlePerPageChange = useCallback((perPage) => {
    setPerPage(perPage).catch(console.error);
  }, [setPerPage]);

  const clearSearch = () => {
    setSearchQuery('');
    setDomainFilter('');
  };

  const handleOpenSearchModal = () => {
    setIsSearchModalOpen(true);
  };

  const handleCloseSearchModal = () => {
    setIsSearchModalOpen(false);
    // Notify search input that modal was just closed to prevent immediate reopen
    if (searchInputRef.current?.notifyModalClosed) {
      searchInputRef.current.notifyModalClosed();
    }
  };


  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <DashboardHeader
        user={user}
        logout={logout}
        userMenuOpen={userMenuOpen}
        setUserMenuOpen={setUserMenuOpen}
        userMenuRef={userMenuRef}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Stats Cards - Playful Clay Style */}
        {stats && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 mb-8 cursor-pointer"
            onClick={() => navigate('/stats')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                navigate('/stats');
              }
            }}
          >
            {/* Total Articles */}
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-fun-sky">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 bg-sky-100 dark:bg-sky-900/40 rounded-clay">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-fun-sky" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="ml-3 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-secondary-text truncate">Total</p>
                  <p className="font-display text-xl sm:text-2xl font-bold text-heading">{stats.total_articles || 0}</p>
                </div>
              </div>
            </div>

            {/* Unread Articles */}
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-accent-500">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 bg-accent-100 dark:bg-accent-900/40 rounded-clay">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-accent-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="ml-3 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-secondary-text truncate">Unread</p>
                  <p className="font-display text-xl sm:text-2xl font-bold text-heading">{stats.unread_articles || 0}</p>
                </div>
              </div>
            </div>

            {/* Read Articles */}
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-fun-mint">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 bg-emerald-100 dark:bg-emerald-900/40 rounded-clay">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-fun-mint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="ml-3 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-secondary-text truncate">Read</p>
                  <p className="font-display text-xl sm:text-2xl font-bold text-heading">{stats.read_articles || 0}</p>
                </div>
              </div>
            </div>

            {/* Weekly Read */}
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-secondary-500">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 bg-secondary-100 dark:bg-secondary-900/40 rounded-clay">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-3 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-secondary-text truncate">Weekly</p>
                  <p className="font-display text-xl sm:text-2xl font-bold text-heading">{stats.weekly_read_articles || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add URL Form */}
        <div className="mb-8">
          <AddUrlForm onSuccess={handleArticleAdded} />
        </div>

        {/* Daily Briefing + Topics — side by side on wide screens */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-2">
          <DailyBriefingWidget />
          <TopicsWidget />
        </div>

        {/* Search and Filters */}
        <div className="card-clay-static mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchInput
                ref={searchInputRef}
                onSearchClick={handleOpenSearchModal}
                placeholder="Search with AI"
              />
            </div>

            <div className="flex items-center space-x-3">

              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="btn-clay-secondary btn-clay-sm flex items-center"
              >
                <svg className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="card-clay-sm border-l-4 border-l-fun-coral bg-rose-50 dark:bg-rose-950/30 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 text-2xl mr-3">
                <span role="img" aria-label="error">😅</span>
              </div>
              <div className="flex-1">
                <p className="text-sm text-rose-700 dark:text-rose-300 font-medium">{error}</p>
              </div>
              <button
                onClick={clearError}
                className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-800/30 rounded-clay transition-all duration-200"
                aria-label="Dismiss error"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Articles Grid */}
        {isLoading && articles.length === 0 ? (
          <div className="text-center py-16">
            <div className="spinner-fun-lg mx-auto mb-4"></div>
            <p className="font-display text-lg text-secondary-text">Loading your articles...</p>
          </div>
        ) : isEmpty ? (
          <EmptyState
            icon="📚"
            title="No articles yet"
            description={searchQuery || domainFilter
              ? 'No articles match your current filters.'
              : "Start by adding your first article using the form above. We'll handle the rest!"}
            action={(searchQuery || domainFilter) ? { label: 'Clear filters', onClick: clearSearch } : undefined}
            className="card-clay-static text-center py-16"
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
              {articles.map((article, index) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  onDelete={handleArticleDeleted}
                  className="stagger-item"
                  style={{ animationDelay: `${index * 50}ms` }}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                isLoading={isLoading}
                className="mt-8"
                itemLabel="articles"
              />
            )}
          </>
        )}
      </main>

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={handleCloseSearchModal}
        savedSearchState={savedSearchState}
        onSearchStateCleared={() => setSavedSearchState(null)}
      />
    </div>
  );
};

export default Dashboard;
